/*
  <page-toc> — builds a table of contents from the h2/h3 headings in the page
  content and keeps the active link highlighted as the reader scrolls.

  Expected DOM (rendered by sections/main-page-toc.liquid):
    <page-toc>
      ...
      <nav data-toc-nav></nav>      -- empty; links are injected here
      <aside data-toc-sidebar>...   -- hidden if the page has no headings
      <div  data-toc-body>...       -- the page content that gets scanned
    </page-toc>
*/
if (!customElements.get('page-toc')) {
  class PageToc extends HTMLElement {
    connectedCallback() {
      this.nav = this.querySelector('[data-toc-nav]');
      this.body = this.querySelector('[data-toc-body]');
      if (!this.nav || !this.body) return;

      const headings = Array.from(this.body.querySelectorAll('h2, h3'));
      if (headings.length === 0) {
        this.classList.add('page-toc--empty');
        return;
      }

      this.links = headings.map((heading, i) => {
        // Generate a stable ID from the text if the heading doesn't have one
        if (!heading.id) {
          heading.id =
            'toc-heading-' +
            i +
            '-' +
            heading.textContent
              .trim()
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-|-$/g, '');
        }

        const a = document.createElement('a');
        a.href = '#' + heading.id;
        a.textContent = heading.textContent.trim();
        a.className =
          'page-toc__link' +
          (heading.tagName === 'H3' ? ' page-toc__link--sub' : '');
        a.setAttribute('data-toc-target', heading.id);
        this.nav.appendChild(a);
        return a;
      });

      // Highlight the active link on scroll
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            this.links.forEach((l) => l.classList.remove('is-active'));
            const activeLink = this.nav.querySelector(
              '[data-toc-target="' + entry.target.id + '"]'
            );
            if (!activeLink) return;
            activeLink.classList.add('is-active');
            this.revealActiveChip(activeLink);
          });
        },
        { rootMargin: '0px 0px -70% 0px', threshold: 0 }
      );

      headings.forEach((h) => this.observer.observe(h));
    }

    revealActiveChip(link) {
      const nav = this.nav;
      if (nav.scrollWidth <= nav.clientWidth) return;

      const target = link.offsetLeft - (nav.clientWidth - link.offsetWidth) / 2;
      const max = nav.scrollWidth - nav.clientWidth;
      nav.scrollTo({
        left: Math.max(0, Math.min(target, max)),
        behavior: 'smooth',
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  customElements.define('page-toc', PageToc);
}
